import { EstimatorTemplateApi } from '@business/api/tm_estimator_template';
import {
  CompactEstimatorTempateResponse,
  EstimatorTempateId,
  EstimatorTemplateStatus,
  EstimatorTemplateType,
  TmEstimatorTempateResponse,
} from '@business/entities/tm/TmTemplateEstimatorResponse';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { askChangeReason } from '@modules/common/ChangeReasonModal';
import { EstimateTemplateFormData } from '@modules/settings/SettingsPage/Components/EstimateTab/AddEditEstimatorTempateModal/EstimateTemplateForm';
import { map, switchMap, tap } from 'rxjs/operators';

export class EstimatorTempateBloc extends BaseBloc {
  private readonly tmFieldEstimateRepo = new Repository<TmEstimatorTempateResponse | CompactEstimatorTempateResponse>({
    getItemId: item => item.id,
  });

  readonly templateEstimators$ = this.tmFieldEstimateRepo.items$;

  readonly activeTemplateEstimators$ = this.tmFieldEstimateRepo.items$.pipe(
    map(templates => templates.filter(u => u.status === EstimatorTemplateStatus.active)),
  );

  // readonly activeSubProjectsTemplateEstimators$ = this.tmFieldEstimateRepo.items$.pipe(
  //   map(templates => templates.filter(u => u.status === EstimatorTemplateStatus.active && u.type === EstimatorTemplateType.subproject)),
  // );

  readonly estimateTemplates$ = this.tmFieldEstimateRepo.items$;
  readonly selectTemplate = this.tmFieldEstimateRepo.selectItem;

  onReset() {
    this.tmFieldEstimateRepo.reset();
  }

  fetchEstimatorTempates = (isCompact = false) => {
    return EstimatorTemplateApi.fetchEstimatorTempates(isCompact).pipe(
      this.tmFieldEstimateRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_estimator_template_error') }),
    );
  };

  fetchEstimatorTempate = (id: EstimatorTempateId) => {
    return EstimatorTemplateApi.fetchEstimatorTempate(id).pipe(
      this.tmFieldEstimateRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_estimator_template_error') }),
    );
  };

  createEstimatorTempate = (data: EstimateTemplateFormData) => {
    return EstimatorTemplateApi.createEstimatorTempate(data).pipe(
      this.tmFieldEstimateRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_field_estimator'),
      }),
    );
  };

  // Edits and deletes ask for the reason first (askChangeReason, kept in the Activity Log); closing that window saves
  // nothing
  updateEstimatorTempate = (id: EstimatorTempateId, data: EstimateTemplateFormData) => {
    return askChangeReason().pipe(
      switchMap(changeNote => EstimatorTemplateApi.updateEstimatorTempate(id, data, changeNote)),
      this.tmFieldEstimateRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_field_estimator'),
      }),
    );
  };

  deleteEstimatorTempate = (id: EstimatorTempateId) => {
    return askChangeReason('Delete Estimate Template', 'Do you really want to remove this estimate template?', 'Delete').pipe(
      switchMap(changeNote => EstimatorTemplateApi.deleteEstimatorTempate(id, changeNote)),
      this.tmFieldEstimateRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_field_estimator'),
      }),
    );
  };
}
